import { useState } from 'react';

import { removeEmoji } from '~/utils/common';

import type { TypeDeliveryAgencyDeliveryLocation, TypeDeliveryAgencyEntranceMethod } from '~/constants';
import {
  COLLECTED_PRODUCT_HANDLING_CODES,
  COLLECTING_REQUEST_CONTACT_TEL_PATTERN,
  COLLECTING_REQUEST_DETAIL_MAX_LENGTH,
  COLLECTING_REQUEST_SECONDARY_ADDRESS_MAX_LENGTH,
  DELIVERY_AGENCY_DELIVERY_LOCATION_DETAIL_REQUIRED_CODES,
  DELIVERY_AGENCY_DELIVERY_LOCATION_OPTIONS,
  DELIVERY_AGENCY_ENTRANCE_METHOD_DETAIL_REQUIRED_CODES,
  DELIVERY_AGENCY_ENTRANCE_METHOD_OPTIONS,
} from '~/constants/delivery-agency';

import type {
  CollectedProductHandling,
  CollectingOutboundDetail,
  CollectingSiteAddressValidation,
  PostCollectingRequestParams,
} from '~/types/delivery-agency.types';

const REQUIRED_INPUT_MESSAGE = '필수 입력입니다.';
const REQUIRED_SELECT_MESSAGE = '필수 선택입니다.';
const TEL_HYPHEN_MESSAGE = "하이픈'-'을 포함해서 올바른 형식으로 입력해주세요.";
const TEL_FORMAT_MESSAGE = '올바른 형식으로 입력해주세요.';
/** 문구 미정의 — 주소 검증 응답을 받기 전에는 등록을 막는다 */
const ADDRESS_UNVERIFIED_MESSAGE = '회수지 주소 확인 중입니다. 잠시 후 다시 시도해 주세요.';
const DELIVERY_LOCATION_DETAIL_MESSAGE =
  "상품회수장소가 '택배 수령실' or '기타' 인 경우 상품회수장소 상세를 필수로 입력해야합니다.";
const ENTRANCE_METHOD_DETAIL_MESSAGE =
  "출입 방법이 '공동현관 비밀번호' or '기타' 인 경우 출입방법 상세를 필수로 입력해야합니다.";

type TypeFormValues = {
  tel: string;
  primaryAddress: string;
  secondaryAddress: string;
  deliveryLocation: TypeDeliveryAgencyDeliveryLocation | null;
  deliveryLocationDetail: string;
  entranceMethod: TypeDeliveryAgencyEntranceMethod | null;
  entranceMethodDetail: string;
  productName: string;
  boxCount: number | null;
  collectedProductHandling: CollectedProductHandling | null;
  sendAlimTalkImmediately: boolean | null;
};

type TypeFormErrors = Partial<Record<keyof TypeFormValues, string>>;

const EMPTY_VALUES: TypeFormValues = {
  tel: '',
  primaryAddress: '',
  secondaryAddress: '',
  deliveryLocation: null,
  deliveryLocationDetail: '',
  entranceMethod: null,
  entranceMethodDetail: '',
  productName: '',
  boxCount: null,
  collectedProductHandling: null,
  sendAlimTalkImmediately: null,
};

/** 프리필 값도 사용자가 직접 입력할 때와 같은 제약을 거친다 */
const toDetailText = (value: string | undefined, maxLength: number) => removeEmoji(value ?? '').slice(0, maxLength);

/** 연락처·주소·장소·방법과 그 상세까지 원주문 값으로 채운다 */
const getInitialValues = (detail: CollectingOutboundDetail): TypeFormValues => ({
  tel: detail.receiverContact ?? '',
  primaryAddress: detail.primaryAddress ?? '',
  secondaryAddress: toDetailText(detail.secondaryAddress, COLLECTING_REQUEST_SECONDARY_ADDRESS_MAX_LENGTH),
  deliveryLocation: detail.deliveryLocation?.code ?? null,
  deliveryLocationDetail: toDetailText(detail.deliveryLocationDetail, COLLECTING_REQUEST_DETAIL_MAX_LENGTH),
  entranceMethod: detail.entranceMethod?.code ?? null,
  entranceMethodDetail: toDetailText(detail.entranceMethodDetail, COLLECTING_REQUEST_DETAIL_MAX_LENGTH),
  productName: '',
  boxCount: null,
  collectedProductHandling: null,
  sendAlimTalkImmediately: null,
});

/**
 * 옵션 목록은 API 응답을 쓰지만 코드값은 고정 enum이라, 상수에서 찾아 좁힌 값만 폼에 담는다.
 * 서버가 모르는 코드를 내려주면 선택되지 않아 필수 검증에서 걸린다.
 */
const narrowCode = <T extends string>(candidates: readonly { value: T }[], code: string): T | null =>
  candidates.find((candidate) => candidate.value === code)?.value ?? null;

/**
 * 연락처 형식 오류를 구분한다. 정책서가 하이픈 개수로 문구를 나눈다.
 */
const getTelError = (tel: string): string | undefined => {
  if (tel === '') return REQUIRED_INPUT_MESSAGE;
  if ((tel.match(/-/g)?.length ?? 0) < 2) return TEL_HYPHEN_MESSAGE;
  if (!COLLECTING_REQUEST_CONTACT_TEL_PATTERN.test(tel)) return TEL_FORMAT_MESSAGE;
  return undefined;
};

/**
 * [반품 신청 2step] 회수 정보 입력 폼 상태 훅
 *
 * 연락처·주소·장소·방법과 그 상세까지 원주문 값으로 초기화한다.
 * 회수지 기본주소는 프리필 값 또는 검색으로만 입력되며, 검색한 주소는 검증 결과가 없거나 회수 불가면 등록을 막는다.
 */
const useCollectingRequestRegisterForm = (detail: CollectingOutboundDetail | null) => {
  const [prefilledOutboundCode, setPrefilledOutboundCode] = useState<string | null>(null);
  const [values, setValues] = useState<TypeFormValues>(EMPTY_VALUES);
  const [errors, setErrors] = useState<TypeFormErrors>({});
  const [addressValidation, setAddressValidation] = useState<CollectingSiteAddressValidation | null>(null);
  const [isAddressSearched, setIsAddressSearched] = useState(false);

  // 원주문 선택이 풀리면 프리필 기준도 비워, 같은 원주문을 다시 골라도 입력값을 버리고 새로 채운다.
  if (!detail && prefilledOutboundCode !== null) {
    setPrefilledOutboundCode(null);
  }

  // 원주문 상세가 도착하거나 다른 원주문으로 바뀌면 폼을 그 값으로 다시 채운다.
  if (detail && detail.outboundCode !== prefilledOutboundCode) {
    setPrefilledOutboundCode(detail.outboundCode);
    setValues(getInitialValues(detail));
    setErrors({});
    setAddressValidation(null);
    setIsAddressSearched(false);
  }

  const isDeliveryLocationDetailRequired =
    values.deliveryLocation !== null &&
    DELIVERY_AGENCY_DELIVERY_LOCATION_DETAIL_REQUIRED_CODES.includes(values.deliveryLocation);
  const isEntranceMethodDetailRequired =
    values.entranceMethod !== null &&
    DELIVERY_AGENCY_ENTRANCE_METHOD_DETAIL_REQUIRED_CODES.includes(values.entranceMethod);

  const setValue = <K extends keyof TypeFormValues>(key: K, value: TypeFormValues[K]) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const handleChangeTel = (value: string) => setValue('tel', value.replace(/[^0-9-]/g, ''));

  const handleChangeDetailText = (
    key: 'secondaryAddress' | 'deliveryLocationDetail' | 'entranceMethodDetail',
    value: string
  ) => setValue(key, removeEmoji(value));

  /** 선택이 바뀌면 이전 선택 기준으로 입력한 상세는 무효라 함께 비운다 */
  const handleChangeDeliveryLocation = (code: string) => {
    const nextCode = narrowCode(DELIVERY_AGENCY_DELIVERY_LOCATION_OPTIONS, code);
    setValues((prev) => ({
      ...prev,
      deliveryLocation: nextCode,
      deliveryLocationDetail: nextCode === prev.deliveryLocation ? prev.deliveryLocationDetail : '',
    }));
    setErrors((prev) => ({ ...prev, deliveryLocation: undefined, deliveryLocationDetail: undefined }));
  };

  const handleChangeEntranceMethod = (code: string) => {
    const nextCode = narrowCode(DELIVERY_AGENCY_ENTRANCE_METHOD_OPTIONS, code);
    setValues((prev) => ({
      ...prev,
      entranceMethod: nextCode,
      entranceMethodDetail: nextCode === prev.entranceMethod ? prev.entranceMethodDetail : '',
    }));
    setErrors((prev) => ({ ...prev, entranceMethod: undefined, entranceMethodDetail: undefined }));
  };

  const handleChangeCollectedProductHandling = (code: string) =>
    setValue('collectedProductHandling', narrowCode(COLLECTED_PRODUCT_HANDLING_CODES, code));

  /** 주소를 다시 검색하면 이전 검증 결과는 무효다 */
  const handleCompleteAddressSearch = (primaryAddress: string) => {
    setValues((prev) => ({ ...prev, primaryAddress }));
    setErrors((prev) => ({ ...prev, primaryAddress: undefined }));
    setAddressValidation(null);
    setIsAddressSearched(true);
  };

  /** 화면설계: 설정 영역에 입력된 값을 모두 지운다. 원주문 프리필 값으로 되돌리지 않는다 */
  const handleResetValues = () => {
    setValues(EMPTY_VALUES);
    setErrors({});
    setAddressValidation(null);
    setIsAddressSearched(false);
  };

  /** 검색한 주소는 검증 응답을 받기 전(null)과 회수 불가 모두 등록을 막는다 */
  const addressError = (() => {
    if (values.primaryAddress === '' || !isAddressSearched) return undefined;
    if (addressValidation === null) return ADDRESS_UNVERIFIED_MESSAGE;
    return addressValidation.available ? undefined : addressValidation.unserviceableReason;
  })();

  /** 검증 응답이 오면 [등록] 때 띄운 '확인 중' 문구는 더 이상 맞지 않아 거둔다 */
  const applyAddressValidation = (validation: CollectingSiteAddressValidation | null) => {
    setAddressValidation(validation);
    setErrors((prev) => ({ ...prev, primaryAddress: undefined }));
  };

  /**
   * 유효성 검사 후 에러를 갱신한다. 통과 시 등록 payload를, 실패 시 null을 반환한다.
   */
  const validate = (): PostCollectingRequestParams | null => {
    if (!detail) return null;

    const nextErrors: TypeFormErrors = {
      tel: getTelError(values.tel),
      primaryAddress: values.primaryAddress === '' ? REQUIRED_INPUT_MESSAGE : addressError,
      deliveryLocation: values.deliveryLocation === null ? REQUIRED_SELECT_MESSAGE : undefined,
      deliveryLocationDetail:
        isDeliveryLocationDetailRequired && values.deliveryLocationDetail.trim() === ''
          ? DELIVERY_LOCATION_DETAIL_MESSAGE
          : undefined,
      entranceMethod: values.entranceMethod === null ? REQUIRED_SELECT_MESSAGE : undefined,
      entranceMethodDetail:
        isEntranceMethodDetailRequired && values.entranceMethodDetail.trim() === ''
          ? ENTRANCE_METHOD_DETAIL_MESSAGE
          : undefined,
      productName: values.productName.trim() === '' ? REQUIRED_INPUT_MESSAGE : undefined,
      boxCount: values.boxCount === null ? REQUIRED_SELECT_MESSAGE : undefined,
      collectedProductHandling: values.collectedProductHandling === null ? REQUIRED_SELECT_MESSAGE : undefined,
      sendAlimTalkImmediately: values.sendAlimTalkImmediately === null ? REQUIRED_SELECT_MESSAGE : undefined,
    };

    setErrors(nextErrors);

    if (Object.values(nextErrors).some(Boolean)) return null;
    if (
      values.deliveryLocation === null ||
      values.entranceMethod === null ||
      values.boxCount === null ||
      values.collectedProductHandling === null ||
      values.sendAlimTalkImmediately === null
    ) {
      return null;
    }

    return {
      outboundCode: detail.outboundCode,
      contact: { name: detail.receiverName, tel: values.tel },
      collectingSite: {
        primaryAddress: values.primaryAddress,
        secondaryAddress: values.secondaryAddress.trim(),
        deliveryLocation: values.deliveryLocation,
        deliveryLocationDetail: values.deliveryLocationDetail.trim(),
        entranceMethod: values.entranceMethod,
        entranceMethodDetail: values.entranceMethodDetail.trim(),
      },
      productName: values.productName.trim(),
      boxCount: values.boxCount,
      collectedProductHandling: values.collectedProductHandling,
      sendAlimTalkImmediately: values.sendAlimTalkImmediately,
    };
  };

  return {
    values,
    errors: {
      ...errors,
      primaryAddress:
        errors.primaryAddress ??
        (addressValidation?.available === false ? addressValidation.unserviceableReason : undefined),
    },
    isDeliveryLocationDetailRequired,
    isEntranceMethodDetailRequired,
    setValue,
    handleChangeTel,
    handleChangeDetailText,
    handleChangeDeliveryLocation,
    handleChangeEntranceMethod,
    handleChangeCollectedProductHandling,
    handleCompleteAddressSearch,
    handleResetValues,
    applyAddressValidation,
    validate,
  };
};

export default useCollectingRequestRegisterForm;
